import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_spread_props from "@swc/helpers/src/_object_spread_props.mjs";
import { formatLocale, resolveMissing } from "./sharedConfig.js";
import { isNumberAutoScaleFormatAutoUnit } from "../api/NumberAutoScaleFormatApi.js";
import { Formatting } from "../implementation/Formatting.js";
import { NumberMissingFormat } from "../implementation/NumberMissingFormat.js";
var largeNumberThreshold = 10000;
var cache = new Map();
var createNumberFormatter = function(param) {
    var currency = param.currency, notation = param.notation, minimumFractionDigits = param.minimumFractionDigits, maximumFractionDigits = param.maximumFractionDigits, unit = param.unit, unitDisplay = param.unitDisplay;
    var style;
    var key = "";
    if (currency) {
        style = "currency";
        key = currency.toUpperCase();
    } else if (unit) {
        style = "unit";
        key = "".concat(unit.toLowerCase(), ",").concat(unitDisplay !== null && unitDisplay !== void 0 ? unitDisplay : "default");
    } else {
        style = "decimal";
    }
    key = "".concat(style, "[").concat(key, "]-").concat(notation !== null && notation !== void 0 ? notation : "standard", "[").concat(minimumFractionDigits !== null && minimumFractionDigits !== void 0 ? minimumFractionDigits : "auto", ",").concat(maximumFractionDigits !== null && maximumFractionDigits !== void 0 ? maximumFractionDigits : "auto", "]");
    var fmt = cache.get(key);
    if (fmt) {
        return fmt;
    }
    var options = {
        missing: resolveMissing(notation === "compact"),
        style: style,
        currency: currency,
        minimumFractionDigits: minimumFractionDigits,
        maximumFractionDigits: maximumFractionDigits,
        unit: unit,
        unitDisplay: unitDisplay
    };
    if (isNumberAutoScaleFormatAutoUnit(unit)) {
        // Limit the default number of maximum digits for compact.
        if (notation === "compact" && typeof options.maximumFractionDigits !== "number") {
            var _minimumFractionDigits;
            options.maximumFractionDigits = Math.max(2, (_minimumFractionDigits = options.minimumFractionDigits) !== null && _minimumFractionDigits !== void 0 ? _minimumFractionDigits : 0);
        }
        // Note: don't combine compact notation with an auto scale selecting format
        fmt = new NumberMissingFormat(formatLocale, options);
    } else {
        fmt = Formatting.hybridNumberFormat(formatLocale, options, largeNumberThreshold, // Note: compact notation needs fraction digits to show a value like '1.25M'.
        {
            notation: notation,
            minimumFractionDigits: 0,
            maximumFractionDigits: notation === "compact" ? 2 : 0
        });
    }
    cache.set(key, fmt);
    return fmt;
};
var createPercentFormatter = function() {
    var ref = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {}, minimumFractionDigits = ref.minimumFractionDigits, maximumFractionDigits = ref.maximumFractionDigits;
    return new NumberMissingFormat(formatLocale, {
        // Always use the "compact" representation of missing for percentages, as their representation is generally short.
        missing: resolveMissing(true),
        style: "percent",
        minimumFractionDigits: minimumFractionDigits,
        maximumFractionDigits: maximumFractionDigits
    });
};
// Don't specify fraction digits for currency, each currency has their own default
var createCurrencyFormatter = function(notation, currency) {
    return createNumberFormatter({
        notation: notation,
        currency: currency
    });
};
var createUnitFormatter = function(notation, options) {
    if (!(options === null || options === void 0 ? void 0 : options.unit)) {
        throw new RangeError("Unit formatters require a unit");
    }
    var _unitDisplay;
    return createNumberFormatter(_object_spread_props(_object_spread({}, options), {
        notation: notation,
        // Select a default unitDisplay based on notation, if not provided.
        unitDisplay: (_unitDisplay = options.unitDisplay) !== null && _unitDisplay !== void 0 ? _unitDisplay : notation === "standard" ? "long" : "short"
    }));
};
/**
 * Contains default Admin-UI number formatters.
 * Note that all formatters come in two "flavors": compact and full.
 * The compact formatter is meant for places with less space, for example titles or axis ticks in charts.
 * The full formatters aim to show the data in full detail, and should be used for tooltips and table cell display of values.
 */ export var NumberFormatter = Object.freeze({
    /**
     * The locale used by these formatters.
     */ formatLocale: formatLocale,
    /**
     * Formatter for numbers that may have decimals, switching to compact notation for higher values.
     */ decimalCompact: createNumberFormatter({
        notation: "compact",
        maximumFractionDigits: 2
    }),
    /**
     * Formatter for numbers that may have decimals.
     */ decimalFull: createNumberFormatter({
        maximumFractionDigits: 2
    }),
    /**
     * Formatter for integer numbers (no decimals), switching to compact notation for higher values.
     */ integerCompact: createNumberFormatter({
        notation: "compact",
        maximumFractionDigits: 0
    }),
    /**
     * Formatter for integer numbers (no decimals).
     */ integerFull: createNumberFormatter({
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }),
    /**
     * Formatter for percentages, rounding to whole integers percentages.
     * Note that `1` represents `100%`, so the value should be the ratio (not multiplied by a hundred).
     */ percentCompact: createPercentFormatter({
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }),
    /**
     * Formatter for percentages, allowing decimals for precision.
     * Note that `1` represents `100%`, so the value should be the ratio (not multiplied by a hundred).
     */ percentFull: createPercentFormatter({
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    }),
    /**
     * Formatter for file sizes (in bytes), automatically selecting the best display scale (kilo, mega, up to peta).
     * Note that this uses binary 2¹⁰ == 1024 scale, with SI units, just like Windows displays sizes.
     */ fileSizeCompact: createUnitFormatter("compact", {
        unit: "auto-bibyte",
        maximumFractionDigits: 1
    }),
    /**
     * Formatter for file sizes (in bytes), automatically selecting the best display scale (kilo, mega, up to peta).
     * Note that this uses binary 2¹⁰ == 1024 scale, with SI units, just like Windows displays sizes.
     */ fileSizeFull: createUnitFormatter("standard", {
        unit: "auto-bibyte",
        maximumFractionDigits: 3
    }),
    /**
     * Get a formatter for a currency, switching to compact notation for higher values.
     * @param currency the three-letter (uppercase) currency code.
     * @returns The formatter for the specified currency.
     */ currencyCompact: function(currency) {
        return createCurrencyFormatter("compact", currency);
    },
    /**
     * Get a formatter for a currency, switching to compact notation for higher values.
     * @param currency the three-letter (uppercase) currency code.
     * @returns The formatter for the specified currency.
     */ currencyFull: function(currency) {
        return createCurrencyFormatter("standard", currency);
    },
    /**
     * Get a formatter for a unit, switching to compact notation for higher values.
     * @param options The options for the formatter, containing at least the unit.
     * @returns The formatter for the specified unit.
     */ unitCompact: function(options) {
        return createUnitFormatter("compact", options);
    },
    /**
     * Get a formatter for a unit, switching to compact notation for higher values.
     * @param options The options for the formatter, containing at least the unit.
     * @returns The formatter for the specified unit.
     */ unitFull: function(options) {
        return createUnitFormatter("standard", options);
    }
});

//# sourceMappingURL=NumberFormatter.js.map